import dayjs from '@/lib/dayjs'
import type { ConfigType, Dayjs } from 'dayjs'
import { isRecord } from '@/lib/utils'

export const REPORT_DATE_MODE = {
  DAY: 'day',
  WEEK: 'week',
  CUSTOM: 'custom',
} as const

export type ReportDateModeValue = (typeof REPORT_DATE_MODE)[keyof typeof REPORT_DATE_MODE]

export interface ReportDateRange {
  start_date: string
  end_date: string
}

const REPORT_DATE_LAUNCH_FALLBACK = dayjs('2025-11-01').startOf('day')

function normalizeReportDateNow(now: ConfigType = dayjs()): Dayjs {
  return dayjs.isDayjs(now) ? now : dayjs(now)
}

// The timezone plugin keeps a zoned instance's zone in its untyped `$x.$timezone`.
function getReportTimezone(now: Dayjs): string | null {
  const internals: unknown = Reflect.get(now, '$x')

  return isRecord(internals) && typeof internals.$timezone === 'string' ? internals.$timezone : null
}

export function getCurrentReportDateFilters(now: ConfigType = dayjs()) {
  const currentDate = normalizeReportDateNow(now)

  return {
    start_date: currentDate.startOf('month').format('YYYY-MM-DD'),
    end_date: currentDate.startOf('day').format('YYYY-MM-DD'),
    date_mode: REPORT_DATE_MODE.CUSTOM,
  }
}

export function clampReportDate(
  date: string,
  minDate?: string | null,
  maxDate?: string | null
): string {
  if (minDate && date < minDate) {
    return minDate
  }

  if (maxDate && date > maxDate) {
    return maxDate
  }

  return date
}

export function clampReportDateRange(
  range: ReportDateRange,
  minDate?: string | null,
  maxDate?: string | null
): ReportDateRange {
  const start_date = clampReportDate(range.start_date, minDate, maxDate)
  const end_date = clampReportDate(range.end_date, minDate, maxDate)

  return start_date <= end_date ? { start_date, end_date } : { start_date, end_date: start_date }
}

export function getOrganizationReportDateBounds(
  organizationCreatedAt: ConfigType | null | undefined,
  now: ConfigType = dayjs(),
  organizationTimezone: string | null = null
) {
  const currentDate = normalizeReportDateNow(now)
  const reportTimezone = organizationTimezone || getReportTimezone(currentDate)
  const organizationCreatedDay = organizationCreatedAt
    ? reportTimezone
      ? dayjs(organizationCreatedAt).tz(reportTimezone).startOf('day')
      : dayjs(organizationCreatedAt).startOf('day')
    : null
  const minDate = organizationCreatedDay || REPORT_DATE_LAUNCH_FALLBACK
  const maxDate = currentDate.endOf('day')

  return {
    minDate: minDate.format('YYYY-MM-DD'),
    maxDate: maxDate.format('YYYY-MM-DD'),
  }
}
